import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import Lottie from 'lottie-react';
import aiBrainAnimation from '../../assets/ai-brain.json';
import {
  Search, Wrench, Rocket, ArrowRight, Globe, Loader2,
  CheckCircle2, Pencil, Check, Eye, Users, Shield,
  Building2, FileText, Zap, Upload, ChevronDown,
} from 'lucide-react';
import { PastSessions } from './shared';

const COUNTRIES = ["Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"];

function CountryAutocomplete({ value, onChange, className }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  const filtered = query
    ? (() => {
        const q = query.toLowerCase();
        const starts = COUNTRIES.filter(c => c.toLowerCase().startsWith(q));
        const contains = COUNTRIES.filter(c => !c.toLowerCase().startsWith(q) && c.toLowerCase().includes(q));
        return [...starts, ...contains].slice(0, 8);
      })()
    : [];

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (country) => { onChange(country); setQuery(country); setOpen(false); setHighlighted(-1); };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); select(filtered[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  useEffect(() => {
    if (highlighted >= 0 && listRef.current) {
      const item = listRef.current.children[highlighted];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted]);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); setHighlighted(-1); }}
          onFocus={() => { if (query) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="e.g., United States"
          dir="ltr"
          className={className}
          data-testid="input-country"
          autoComplete="off"
        />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>
      {open && filtered.length > 0 && (
        <ul ref={listRef} className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg" data-testid="country-suggestions">
          {filtered.map((c, i) => (
            <li
              key={c}
              onMouseDown={() => select(c)}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${i === highlighted ? 'bg-odapto-orange/10 text-odapto-orange' : 'hover:bg-muted'}`}
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const JOURNEY_OPTIONS = [
  { value: 'find_opportunities', label: 'Find Opportunities', icon: Search, color: 'border-blue-500/30 bg-blue-500/5' },
  { value: 'fix_problems', label: 'Fix Business Problems', icon: Wrench, color: 'border-amber-500/30 bg-amber-500/5' },
  { value: 'accelerate_growth', label: 'Accelerate Growth', icon: Rocket, color: 'border-emerald-500/30 bg-emerald-500/5' },
];

const STRATEGIC_QUESTIONS = [
  { key: 'q1', label: 'What does your company do, for whom, and how do you make money?' },
  { key: 'q2', label: 'What are you trying to achieve in the next 12–24 months?' },
  { key: 'q3', label: 'What is the biggest issue keeping you awake right now?' },
  { key: 'q4', label: 'Where do you believe the biggest untapped opportunity is?' },
  { key: 'q5', label: 'What do you suspect might be happening in your business that you don\'t fully understand yet?' },
];

export function OnboardingForm({ userName, onSubmit, initialData }) {
  const [form, setForm] = useState(() => {
    if (initialData) {
      const si = initialData.strategic_intake || {};
      return {
        company_name: initialData.company_name || '',
        country: initialData.country || '',
        website: initialData.website || '',
        journey_type: initialData.journey_type || '',
        strategic_intake: {
          q1: si.q1 || '', q2: si.q2 || '', q3: si.q3 || '',
          q4: si.q4 || '', q5: si.q5 || '',
        },
      };
    }
    return {
      company_name: '', country: '', website: '', journey_type: '',
      strategic_intake: { q1: '', q2: '', q3: '', q4: '', q5: '' },
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setIntake = (k, v) => setForm(p => ({ ...p, strategic_intake: { ...p.strategic_intake, [k]: v } }));

  const intakeAnswered = Object.values(form.strategic_intake).filter(v => v.trim().length > 3).length >= 3;
  const canSubmit = form.company_name.trim() && form.country.trim() && (form.journey_type || intakeAnswered);

  const getGreeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };
  const firstName = userName?.split(' ')[0];

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    // Derive challenge from Q3 for backward compat
    const challenge = form.strategic_intake.q3 || '';
    await onSubmit({ ...form, challenge });
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="onboarding-form">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-odapto-orange/10 flex items-center justify-center mx-auto mb-4">
          <Lottie animationData={aiBrainAnimation} loop autoplay style={{ width: 44, height: 44 }} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">{getGreeting()}{firstName ? `, ${firstName}` : ''}.</h1>
        <p className="text-lg text-muted-foreground">Let&apos;s get to know your business.</p>
        <p className="text-sm text-muted-foreground mt-1">This takes less than 2 minutes.</p>
      </div>
      <div className="space-y-6">
        {/* Company Name */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Company Name <span className="text-red-500">*</span></label>
          <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="e.g., Acme Corp" dir="ltr" className="text-base" data-testid="input-company-name" />
        </div>
        {/* Country */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Country <span className="text-red-500">*</span></label>
          <CountryAutocomplete value={form.country} onChange={v => set('country', v)} className="text-base" />
        </div>
        {/* Company Website (moved below Country) */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">
            Company Website <span className="text-muted-foreground text-xs font-normal">(strongly recommended)</span>
          </label>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="e.g., www.yourcompany.com" dir="ltr" className="text-base flex-1" data-testid="input-website" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Providing your website dramatically improves AI recommendations.</p>
        </div>

        {/* Strategic Intake Questions */}
        <div className="border-t border-border pt-6">
          <p className="text-sm font-medium text-muted-foreground mb-4">Help MrCanvas understand where to focus first.</p>
          {STRATEGIC_QUESTIONS.map(sq => (
            <div key={sq.key} className="mb-4">
              <label className="text-sm font-medium mb-1.5 block">{sq.label}</label>
              <textarea
                value={form.strategic_intake[sq.key]}
                onChange={e => setIntake(sq.key, e.target.value)}
                placeholder="Type your answer..."
                className="w-full min-h-[60px] p-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30 focus:border-odapto-orange text-left"
                dir="ltr"
                data-testid={`input-${sq.key}`}
              />
            </div>
          ))}
        </div>

        {/* Journey Buttons */}
        <div>
          <label className="text-sm font-medium mb-1.5 block">Or do you want to explore one of the 3 areas?</label>
          <div className="grid gap-2 sm:grid-cols-3">
            {JOURNEY_OPTIONS.map(jo => {
              const Icon = jo.icon;
              const sel = form.journey_type === jo.value;
              return (
                <button key={jo.value} onClick={() => set('journey_type', sel ? '' : jo.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${sel ? 'border-odapto-orange bg-odapto-orange/10' : `${jo.color} hover:border-odapto-orange/40`}`}
                  data-testid={`journey-${jo.value}`}>
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${sel ? 'text-odapto-orange' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-medium ${sel ? 'text-odapto-orange' : ''}`}>{jo.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">Optional — MrCanvas will suggest the best focus based on your answers above.</p>
        </div>

        <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
          className="w-full bg-odapto-orange hover:bg-odapto-orange-hover text-white py-3 text-base rounded-xl" data-testid="onboard-submit-btn">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Starting...</> : <>Build into Start <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
      <PastSessions />
    </div>
  );
}

const RESEARCH_STEPS = [
  { key: 'reading_website', label: 'Reading website', icon: Globe },
  { key: 'understanding_products', label: 'Understanding products', icon: Eye },
  { key: 'identifying_services', label: 'Identifying services', icon: FileText },
  { key: 'analysing_customers', label: 'Analysing customers', icon: Users },
  { key: 'analysing_competitors', label: 'Analysing competitors', icon: Shield },
  { key: 'detecting_model', label: 'Detecting business model', icon: Building2 },
  { key: 'building_profile', label: 'Building Company Profile', icon: FileText },
  { key: 'creating_kb', label: 'Creating Company Knowledge Base', icon: Zap },
];

export function ResearchProgress({ jobId, onComplete }) {
  const [job, setJob] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 60; i++) {
        if (cancelled) return;
        try {
          const res = await apiGet(`/company/research-job/${jobId}`);
          if (res.ok) {
            const data = await res.json();
            setJob(data);
            const doneSteps = (data.steps || []).filter(s => s.status === 'done').length;
            setActiveIdx(doneSteps);
            if (data.status === 'complete') { onComplete(data); return; }
            if (data.status === 'failed') { toast.error('Research failed. Please try again.'); return; }
          }
        } catch { /* retry */ }
        await new Promise(r => setTimeout(r, 1500));
      }
      toast.error('Research timed out.');
    };
    poll();
    return () => { cancelled = true; };
  }, [jobId, onComplete]);

  return (
    <div className="max-w-xl mx-auto px-4 flex flex-col items-center justify-center min-h-[70vh]" data-testid="research-progress">
      <div className="w-20 h-20 rounded-2xl bg-odapto-orange/10 flex items-center justify-center mb-6">
        <Lottie animationData={aiBrainAnimation} loop autoplay style={{ width: 56, height: 56 }} />
      </div>
      <h2 className="text-2xl font-bold mb-2 text-center">Building your Company Intelligence...</h2>
      <p className="text-sm text-muted-foreground mb-8 text-center">Our AI is analysing your business. This takes about 30 seconds.</p>
      <div className="w-full space-y-3">
        {RESEARCH_STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = i < activeIdx;
          const active = i === activeIdx;
          const skipped = (job?.steps || []).find(s => s.step === step.key)?.status === 'skipped';
          return (
            <div key={step.key} className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${done ? 'bg-emerald-500/5 border border-emerald-500/20' : active ? 'bg-odapto-orange/5 border border-odapto-orange/20' : 'opacity-40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500/20 text-emerald-500' : active ? 'bg-odapto-orange/20 text-odapto-orange' : 'bg-muted text-muted-foreground'}`}>
                {done ? <Check className="w-4 h-4" /> : active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-sm font-medium ${done ? 'text-emerald-600 dark:text-emerald-400' : active ? 'text-odapto-orange' : 'text-muted-foreground'}`}>
                {skipped ? `${step.label} (skipped)` : step.label}
              </span>
              {done && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CompanySummary({ kb, onConfirm, onEdit, onAddMore }) {
  const [confirming, setConfirming] = useState(false);
  if (!kb) return null;

  const profile = kb.profile || {};
  const confidence = kb.confidence_score || 0;
  const missing = kb.missing_info || [];
  const confColor = confidence >= 80 ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/5' : confidence >= 60 ? 'text-amber-500 border-amber-500/30 bg-amber-500/5' : 'text-red-500 border-red-500/30 bg-red-500/5';

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await apiPost('/company/confirm-kb', {});
      if (res.ok) onConfirm();
      else toast.error('Failed to confirm');
    } catch { toast.error('Connection error'); }
    setConfirming(false);
  };

  const fields = [
    { label: 'Industry', val: profile.industry },
    { label: 'Business Model', val: profile.business_model },
    { label: 'Products', val: Array.isArray(profile.products) ? profile.products.map(p => typeof p === 'object' ? p.name : p).join(', ') : profile.products },
    { label: 'Services', val: Array.isArray(profile.services) ? profile.services.map(s => typeof s === 'object' ? s.name : s).join(', ') : profile.services },
    { label: 'Target Customers', val: profile.target_customers },
    { label: 'Value Proposition', val: profile.value_proposition },
    { label: 'Market Position', val: profile.market_position },
    { label: 'Competitors', val: Array.isArray(profile.competitors) ? profile.competitors.map(c => typeof c === 'object' ? c.name : c).join(', ') : profile.competitors },
    { label: 'Geographical Presence', val: profile.geographical_presence },
  ].filter(f => f.val && f.val !== 'null');

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="company-summary">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-1">We&apos;ve analysed your company.</h1>
        <p className="text-muted-foreground">Here&apos;s what we understand about <strong>{kb.company_name}</strong>.</p>
      </div>
      <div className="flex items-center justify-center gap-3 mb-6">
        <span className="text-sm text-muted-foreground">Business Understanding</span>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${confColor}`}><span className="text-xl font-bold">{confidence}%</span></div>
      </div>
      {profile.company_overview && (
        <div className="rounded-xl border border-odapto-orange/20 bg-gradient-to-br from-odapto-orange/5 to-amber-500/5 p-5 mb-6">
          <p className="text-base leading-relaxed">{profile.company_overview}</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        {fields.map(f => (
          <div key={f.label} className="p-3 rounded-lg border border-border bg-card">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{f.label}</p>
            <p className="text-sm">{f.val}</p>
          </div>
        ))}
      </div>
      {missing.length > 0 && confidence < 85 && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-50 dark:bg-amber-900/10 p-4 mb-6">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">Missing Information</h3>
          <div className="flex flex-wrap gap-2">
            {missing.map(m => (<span key={m.key} className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">{m.label}</span>))}
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">You can provide these later when needed for specific advice.</p>
        </div>
      )}
      {/* Uploaded Documents */}
      {(kb.documents || []).length > 0 && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-50 dark:bg-emerald-900/10 p-4 mb-6">
          <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">Uploaded Documents</h3>
          <div className="space-y-1.5">
            {kb.documents.map(d => (
              <div key={d.doc_id} className="flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span className="font-medium">{d.filename}</span>
                <span className="text-muted-foreground">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-center gap-3 flex-wrap">
        <Button variant="outline" onClick={onEdit} data-testid="summary-edit-btn"><Pencil className="w-4 h-4 mr-2" /> Edit Information</Button>
        {onAddMore && (
          <Button variant="outline" onClick={onAddMore} data-testid="summary-add-more-btn"><Upload className="w-4 h-4 mr-2" /> Add More Information</Button>
        )}
        <Button onClick={handleConfirm} disabled={confirming} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-6" data-testid="summary-confirm-btn">
          {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Continue
        </Button>
      </div>
    </div>
  );
}
